import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, X, Send, User, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { PraxisPaper, UserProfile } from '../types';
import { examCatalog, ExamCatalogEntry } from '../data/examCatalog';
import { CHAT_FAQS, ChatFaq, ChatActionKey, HANDOFF_PHRASES, PAPER_DETAIL_PHRASES } from '../data/chatFaqs';
import { papersForFamily } from './brand/FamilyArt';
import { getPaperCatalogSummaries, getPreviewAuthHeaders, isUserAdmin, isUserSubscribed } from '../lib/firebase';

// =====================================================================
// Live chat (2026-10-25, Round 1). Replaces the old keyword script.
// No AI: answers come from the FAQ list (src/data/chatFaqs.ts) and from the
// live paper list (description, topics, length, timing, practice pool,
// mock exams), so everything it says is true and costs nothing to run.
// Every answer ends with buttons that fit the visitor (guest, free member
// or subscriber), and "Talk to a person" offers WhatsApp, the free
// consultation or a message. The first message of each chat and every
// "leave a message" are emailed to the admin inbox via /api/chat/notify.
// =====================================================================

const WHATSAPP_NUMBER = '16615310357';
const WHATSAPP_DISPLAY = '+1 661 531 0357';

type VisitorType = 'guest' | 'free' | 'subscriber';

interface ChatAction {
  label: string;
  run: () => void;
  primary?: boolean;
}

interface ChatMessage {
  from: 'bot' | 'user';
  title?: string;
  text: string;
  actions?: ChatAction[];
  showHandoffForm?: boolean;
}

interface FloatingSupportProps {
  onOpenPricingModal: () => void;
  onOpenAuthModal: (mode?: 'signup' | 'signin') => void;
  userProfile?: UserProfile;
  papers?: PraxisPaper[];
  activeTab?: string;
  selectedExamEntry?: ExamCatalogEntry | null;
  onTryFree?: (paperId: string) => void;
  onStartTest?: (paperId: string) => void;
  onSelectExam?: (entry: ExamCatalogEntry) => void;
  setActiveTab?: (tab: string) => void;
}

// ---------------- text matching helpers ----------------
const norm = (s: string) => ` ${String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim()} `;
const has = (text: string, phrase: string) => {
  const p = norm(phrase).trim();
  return p.length > 0 && text.includes(` ${p} `);
};
const GENERIC_TITLE_WORDS = new Set([
  'education', 'elementary', 'content', 'knowledge', 'praxis', 'core', 'academic', 'skills', 'educators', 'teaching',
  'test', 'exam', 'subject', 'assessment', 'grades', 'grade', 'principles', 'learning', 'and', 'the', 'for', 'with',
  'students', 'applications', 'analysis', 'subtest', 'multiple', 'subjects', 'foundational', 'interpretation',
  'design', 'concentration', 'fundamentals', 'specialist', 'young', 'children'
]);
const titleWords = (p: PraxisPaper) =>
  norm(p.title).trim().split(' ').filter((w) => w.length >= 4 && !GENERIC_TITLE_WORDS.has(w));

function scoreFaq(text: string, faq: ChatFaq): number {
  let score = 0;
  for (const k of faq.keywords) {
    if (has(text, k)) score += norm(k).trim().split(' ').length;
  }
  return score;
}

function paperLabel(p: PraxisPaper): string {
  const code = String(p.code || '').trim();
  return /^\d{4}$/.test(code) ? `Praxis ${code}` : code;
}

function familyOf(p: PraxisPaper): ExamCatalogEntry | undefined {
  const hit = examCatalog.find((e) => papersForFamily(e, [p]).length > 0);
  if (hit) return hit;
  if (String(p.examCode || '').toLowerCase().startsWith('praxis')) return examCatalog.find((e) => e.id === 'most-other-states');
  return undefined;
}

const FAMILY_WORDS: Record<string, string[]> = {
  'most-other-states': ['praxis series', 'praxis subject', 'subject tests'],
  california: ['california', 'cbest', 'cset', 'ctc'],
  'paraprofessional-pathway': ['parapro', 'parapathways', 'paraprofessional', 'para educator', 'paraeducator'],
  'new-york': ['new york', 'nystce', 'eas'],
  texas: ['texas', 'texes'],
  florida: ['florida', 'ftce'],
  illinois: ['illinois', 'ilts'],
  pennsylvania: ['pennsylvania', 'pect', 'papa'],
  georgia: ['georgia', 'gace']
};

function newSessionId(): string {
  try {
    const existing = sessionStorage.getItem('pp_chat_session');
    if (existing) return existing;
    const id = 'chat_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
    sessionStorage.setItem('pp_chat_session', id);
    return id;
  } catch {
    return 'chat_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
  }
}

export const FloatingSupport: React.FC<FloatingSupportProps> = ({
  onOpenPricingModal,
  onOpenAuthModal,
  userProfile,
  papers: papersProp,
  activeTab,
  selectedExamEntry,
  onTryFree,
  onStartTest,
  onSelectExam,
  setActiveTab
}) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [papers, setPapers] = useState<PraxisPaper[]>(papersProp || []);
  const [userCount, setUserCount] = useState(0);
  const [nudged, setNudged] = useState<{ guest?: boolean; plans?: boolean }>({});
  const [salesInterest, setSalesInterest] = useState(0);
  const [form, setForm] = useState({ name: '', contact: '', message: '' });
  const [formState, setFormState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const startedSent = useRef(false);
  const sessionId = useMemo(() => newSessionId(), []);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const visitor: VisitorType = !userProfile?.uid || userProfile.uid === 'guest_user_1'
    ? 'guest'
    : (isUserAdmin(userProfile) || isUserSubscribed(userProfile)) ? 'subscriber' : 'free';

  useEffect(() => {
    if (papersProp && papersProp.length > 0) setPapers(papersProp);
  }, [papersProp]);

  useEffect(() => {
    if (!open || papers.length > 0) return;
    getPaperCatalogSummaries().then((p) => { if (Array.isArray(p) && p.length) setPapers(p); }).catch(() => {});
  }, [open, papers.length]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, open]);

  useEffect(() => {
    if (userProfile?.displayName || userProfile?.email) {
      setForm((f) => ({
        ...f,
        name: f.name || (visitor !== 'guest' ? userProfile?.displayName || '' : ''),
        contact: f.contact || (visitor !== 'guest' ? userProfile?.email || '' : '')
      }));
    }
  }, [userProfile?.uid]);

  const livePapers = useMemo(() => {
    const ids = new Set<string>();
    const out: PraxisPaper[] = [];
    examCatalog.forEach((e) => papersForFamily(e, papers).forEach((p) => { if (!ids.has(p.id)) { ids.add(p.id); out.push(p); } }));
    // bundles (5001) have no questions of their own but are still a valid answer
    papers.forEach((p) => { if (Array.isArray(p.bundleOf) && p.bundleOf.length && !ids.has(p.id)) { ids.add(p.id); out.push(p); } });
    return out;
  }, [papers]);

  // ---------------- buttons ----------------
  const openWhatsApp = () => {
    const text = encodeURIComponent('Hello PrePraxis, I have a question about my teacher certification prep.');
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, '_blank', 'noopener,noreferrer');
  };
  const goTab = (tab: string) => { setActiveTab?.(tab); setOpen(false); window.scrollTo?.(0, 0); };
  const goSignup = () => { onOpenAuthModal('signup'); setOpen(false); };
  const showHandoff = () => {
    setMessages((m) => [...m, {
      from: 'bot',
      title: 'Talk to a person',
      text: `We are happy to help. Choose how you would like to reach us, or leave a message and we will reply by email or WhatsApp.`,
      actions: [
        { label: `WhatsApp ${WHATSAPP_DISPLAY}`, run: openWhatsApp, primary: true },
        { label: 'Book a free consultation', run: () => goTab('tutoring') }
      ],
      showHandoffForm: true
    }]);
  };

  const resolveAction = (key: ChatActionKey, paper?: PraxisPaper): ChatAction | null => {
    const signedIn = visitor !== 'guest';
    switch (key) {
      case 'tryFree':
        if (visitor === 'subscriber') {
          return paper && onStartTest
            ? { label: `Start practicing ${paperLabel(paper)}`, run: () => { onStartTest(paper.id); setOpen(false); }, primary: true }
            : { label: 'Browse exams', run: () => goTab('papers') };
        }
        return paper && onTryFree
          ? { label: `Try 5 free questions on ${paperLabel(paper)}`, run: () => { onTryFree(paper.id); setOpen(false); }, primary: true }
          : { label: 'Try 5 free questions', run: () => goTab('papers'), primary: true };
      case 'signup':
        return signedIn ? null : { label: 'Create free account', run: goSignup, primary: true };
      case 'login':
        return signedIn ? null : { label: 'Log in', run: () => { onOpenAuthModal('signin'); setOpen(false); } };
      case 'pricing':
        return visitor === 'subscriber' ? null : { label: 'See plans', run: () => { onOpenPricingModal(); setOpen(false); } };
      case 'exams':
        return { label: 'Browse exams', run: () => goTab('papers') };
      case 'mocks':
        return signedIn ? { label: 'Open mock exams', run: () => goTab('mock-exams') } : { label: 'Create free account', run: goSignup };
      case 'guides':
        return signedIn ? { label: 'Open study guides', run: () => goTab('guides') } : { label: 'Create free account', run: goSignup };
      case 'flashcards':
        return signedIn ? { label: 'Find my exam', run: () => goTab('papers') } : { label: 'Create free account', run: goSignup };
      case 'review':
        return signedIn ? { label: 'Open review queue', run: () => goTab('review') } : { label: 'Create free account', run: goSignup };
      case 'dashboard':
        return signedIn ? { label: 'Open my dashboard', run: () => goTab('dashboard') } : { label: 'Create free account', run: goSignup };
      case 'tutoring':
        return { label: 'Book a free consultation', run: () => goTab('tutoring') };
      case 'whatsapp':
        return { label: 'Chat on WhatsApp', run: openWhatsApp };
      case 'handoff':
        return { label: 'Leave a message', run: showHandoff };
      default:
        return null;
    }
  };

  const buildActions = (keys: ChatActionKey[], paper?: PraxisPaper): ChatAction[] => {
    const seen = new Set<string>();
    const out: ChatAction[] = [];
    for (const k of keys) {
      const a = resolveAction(k, paper);
      if (a && !seen.has(a.label)) { seen.add(a.label); out.push(a); }
    }
    return out.slice(0, 3);
  };

  // ---------------- answers ----------------
  const fillTokens = (text: string) => {
    const live = examCatalog.filter((e) => papersForFamily(e, papers).length > 0).map((e) => e.shortLabel);
    const soon = examCatalog.filter((e) => papersForFamily(e, papers).length === 0).map((e) => e.shortLabel);
    const list = (a: string[]) => (a.length <= 1 ? a.join('') : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`);
    return text
      .replace('{paperCount}', String(livePapers.filter((p) => !p.bundleOf).length || 'many'))
      .replace('{liveFamilies}', list(live) || 'the Praxis series')
      .replace('{soonFamilies}', list(soon) || 'more states');
  };

  const paperAnswer = (p: PraxisPaper): ChatMessage => {
    const lines: string[] = [];
    if (p.description) lines.push(p.description);
    if (Array.isArray(p.bundleOf) && p.bundleOf.length) {
      const subs = p.bundleOf
        .map((id) => papers.find((x) => x.id === id || x.code === id))
        .filter(Boolean) as PraxisPaper[];
      lines.push(`It is taken as ${p.bundleOf.length} separate subtests, each timed and scored on its own:`);
      subs.forEach((s) => lines.push(`• ${paperLabel(s)}: ${s.title.replace(/^Elementary Education:\s*/i, '')}`));
      return {
        from: 'bot',
        title: `${paperLabel(p)}: ${p.title}`,
        text: lines.join('\n'),
        actions: subs.slice(0, 2).map((s) => resolveAction('tryFree', s)).filter(Boolean) as ChatAction[]
      };
    }
    const facts: string[] = [];
    if (typeof p.questionCount === 'number' && p.questionCount > 0) facts.push(`${p.questionCount} questions`);
    if (p.durationMinutes > 0) facts.push(`${p.durationMinutes} minutes`);
    if (facts.length) lines.push(`• The test: ${facts.join(', ')}`);
    const doms = (p.domains || []).filter((d) => d && d.name && d.name !== 'General');
    if (doms.length) {
      lines.push(`• Topics: ${doms.slice(0, 6).map((d) => (d.weightPercentage ? `${d.name} (${Math.round(d.weightPercentage)}%)` : d.name)).join('; ')}`);
    }
    const pool = p.totalQuestions || 0;
    const hasMocks = typeof p.questionCount === 'number' && p.questionCount > 0 && pool >= p.questionCount * 2;
    if (pool > 0) {
      lines.push(`• On PrePraxis: ${pool} practice questions with explanations${hasMocks ? ', plus 2 full timed mock exams' : ''}`);
    }
    if (visitor === 'guest') lines.push('You can try 5 of these questions right now, no sign up needed.');
    else if (visitor === 'free') lines.push('Your free questions on this paper are waiting. A plan unlocks every question and both mock exams.');
    const fam = familyOf(p);
    const actions = buildActions(['tryFree', visitor === 'subscriber' ? 'mocks' : 'pricing'], p);
    if (fam && onSelectExam) actions.push({ label: `Open ${paperLabel(p)} page`, run: () => { onSelectExam(fam); setOpen(false); } });
    return { from: 'bot', title: `${paperLabel(p)}: ${p.title}`, text: lines.join('\n'), actions: actions.slice(0, 3) };
  };

  const familyAnswer = (e: ExamCatalogEntry): ChatMessage => {
    const fp = papersForFamily(e, papers);
    if (fp.length === 0) {
      return {
        from: 'bot',
        title: e.shortLabel,
        text: `${e.shortLabel} (${e.state}) practice is coming soon. Leave us a message and we will let you know when it is ready, or book a free consultation for help in the meantime.`,
        actions: buildActions(['handoff', 'tutoring'])
      };
    }
    const codes = fp.slice(0, 8).map((p) => paperLabel(p)).join(', ');
    return {
      from: 'bot',
      title: `${e.shortLabel} on PrePraxis`,
      text: `We have ${fp.length} ${e.shortLabel} paper${fp.length === 1 ? '' : 's'} with practice questions${fp.length > 8 ? `, including ${codes}` : `: ${codes}`}. Tell me your exact test code for the details.`,
      actions: [
        ...(onSelectExam ? [{ label: `Open ${e.shortLabel}`, run: () => { onSelectExam(e); setOpen(false); }, primary: true }] : []),
        ...buildActions(['tryFree', 'pricing'])
      ].slice(0, 3)
    };
  };

  const faqAnswer = (f: ChatFaq, paper?: PraxisPaper): ChatMessage => ({
    from: 'bot',
    title: f.question,
    text: fillTokens(f.answer),
    actions: buildActions(f.actions, paper)
  });

  const findPapers = (text: string): { exact?: PraxisPaper; candidates: PraxisPaper[] } => {
    const all = livePapers;
    const tokens = text.trim().split(' ');
    for (const t of tokens) {
      if (/^\d{4}$/.test(t)) {
        const hit = all.find((p) => p.code === t) || papers.find((p) => p.code === t);
        if (hit) return { exact: hit, candidates: [] };
      }
    }
    for (const p of all) {
      const code = norm(p.code).trim();
      if (code && !/^\d{4}$/.test(code) && code.includes(' ') && has(text, code)) return { exact: p, candidates: [] };
    }
    let best = 0;
    let scored: { p: PraxisPaper; s: number }[] = [];
    for (const p of all) {
      const s = titleWords(p).filter((w) => has(text, w)).length;
      if (s > 0) scored.push({ p, s });
      if (s > best) best = s;
    }
    scored = scored.filter((x) => x.s === best);
    if (scored.length === 1) return { exact: scored[0].p, candidates: [] };
    return { candidates: scored.slice(0, 5).map((x) => x.p) };
  };

  const answerFor = (raw: string): ChatMessage[] => {
    const text = norm(raw);
    if (HANDOFF_PHRASES.some((h) => has(text, h))) {
      return [{
        from: 'bot',
        title: 'Talk to a person',
        text: 'Of course. Choose how you would like to reach us, or leave a message and we will reply by email or WhatsApp.',
        actions: [
          { label: `WhatsApp ${WHATSAPP_DISPLAY}`, run: openWhatsApp, primary: true },
          { label: 'Book a free consultation', run: () => goTab('tutoring') }
        ],
        showHandoffForm: true
      }];
    }

    let bestFaq: ChatFaq | null = null;
    let bestScore = 0;
    for (const f of CHAT_FAQS) {
      const s = scoreFaq(text, f);
      if (s > bestScore) { bestScore = s; bestFaq = f; }
    }
    const { exact, candidates } = findPapers(text);
    const wantsDetails = PAPER_DETAIL_PHRASES.some((d) => has(text, d));

    if (exact) {
      if (bestFaq && bestScore >= 1 && ['pricing', 'support', 'account'].includes(bestFaq.category) && !wantsDetails) {
        return [faqAnswer(bestFaq, exact)];
      }
      const out = [paperAnswer(exact)];
      if (bestFaq && bestScore >= 1 && bestFaq.category === 'product') out.push({ ...faqAnswer(bestFaq, exact), actions: [] });
      return out;
    }

    const family = examCatalog.find((e) => (FAMILY_WORDS[e.id] || []).some((w) => has(text, w)));
    if (family && !(bestFaq && bestScore >= 2)) return [familyAnswer(family)];

    if (bestFaq && bestScore >= 1) return [faqAnswer(bestFaq)];

    if (candidates.length > 0) {
      return [{
        from: 'bot',
        title: 'Which paper do you mean?',
        text: 'I found a few papers that match. Pick one and I will show you what it covers.',
        actions: candidates.slice(0, 4).map((p) => ({ label: `${paperLabel(p)}: ${p.title}`.slice(0, 60), run: () => ask(p.code) }))
      }];
    }

    // Nothing matched: closest FAQs by shared words, plus a person.
    const words = text.trim().split(' ').filter((w) => w.length >= 4);
    const near = CHAT_FAQS
      .map((f) => ({ f, s: words.filter((w) => norm(f.question + ' ' + f.keywords.join(' ')).includes(` ${w}`)).length }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 3);
    return [{
      from: 'bot',
      title: near.length ? 'Did you mean...?' : 'Let me point you the right way',
      text: near.length
        ? 'I am not completely sure what you are asking. One of these may help, or a person can answer you directly.'
        : 'I could not find an answer to that. For a study question, the quickest help is to practise the topic or ask our tutors. You can also tell me your exam code (for example 5733).',
      actions: [
        ...near.map((x) => ({ label: x.f.question, run: () => ask(x.f.question) })),
        ...(near.length ? [] : buildActions(['tryFree', 'tutoring'])),
        { label: 'Talk to a person', run: showHandoff }
      ].slice(0, 4)
    }];
  };

  // ---------------- notify admin (first message, and handoff) ----------------
  const notify = async (payload: Record<string, any>) => {
    try {
      const headers = visitor === 'guest' ? {} : await getPreviewAuthHeaders();
      await fetch('/api/chat/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ sessionId, visitor, page: activeTab || 'landing', ...payload })
      });
    } catch {
      /* best effort only */
    }
  };

  const transcript = (msgs: ChatMessage[]) =>
    msgs.slice(-20).map((m) => ({ from: m.from, text: `${m.title ? m.title + ': ' : ''}${m.text}`.slice(0, 600) }));

  // ---------------- sending a message ----------------
  const ask = (raw: string) => {
    const q = raw.trim();
    if (!q) return;
    const replies = answerFor(q);
    const nextCount = userCount + 1;
    const extra: ChatMessage[] = [];

    const salesTopic = replies.some((r) => /plans|mock|guide|flashcard|subscription|cost|Praxis \d{4}/i.test(r.title || ''));
    const nextInterest = salesInterest + (salesTopic ? 1 : 0);

    const isHandoff = replies.some((r) => r.showHandoffForm);
    if (isHandoff) {
      // never push a sales note under the contact form
    } else if (visitor === 'guest' && nextCount >= 3 && !nudged.guest) {
      extra.push({
        from: 'bot',
        title: 'A study plan, free',
        text: 'Want a week by week study plan built around your exam date? Create a free account. It takes a minute, and you get 5 free questions on every paper.',
        actions: buildActions(['signup', 'tryFree'])
      });
      setNudged((n) => ({ ...n, guest: true }));
    } else if (visitor === 'free' && nextInterest >= 2 && !nudged.plans) {
      extra.push({
        from: 'bot',
        title: 'Unlock everything',
        text: 'All Access unlocks every question, both full mock exams and the study guides on every paper. Plans start at $28 a month on the 4 month plan.',
        actions: buildActions(['pricing'])
      });
      setNudged((n) => ({ ...n, plans: true }));
    }

    const userMsg: ChatMessage = { from: 'user', text: q };
    setMessages((m) => [...m, userMsg, ...replies, ...extra]);
    setUserCount(nextCount);
    setSalesInterest(nextInterest);
    setInput('');

    if (!startedSent.current) {
      startedSent.current = true;
      notify({ kind: 'started', firstMessage: q.slice(0, 1000), botReply: (replies[0]?.title || replies[0]?.text || '').slice(0, 300) });
    }
  };

  const submitHandoff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.contact.trim() || !form.message.trim()) return;
    setFormState('sending');
    await notify({
      kind: 'handoff',
      contact: { name: form.name.trim().slice(0, 120), contact: form.contact.trim().slice(0, 200), message: form.message.trim().slice(0, 2000) },
      transcript: transcript(messages)
    });
    setFormState('sent');
    setMessages((m) => [...m, {
      from: 'bot',
      title: 'Message sent',
      text: `Thank you${form.name ? `, ${form.name.split(' ')[0]}` : ''}. We have your message and will reply to ${form.contact.trim()} as soon as we can. For a faster answer, WhatsApp us on ${WHATSAPP_DISPLAY}.`
    }]);
  };

  // ---------------- welcome ----------------
  const openChat = () => {
    setOpen((o) => !o);
    if (messages.length > 0) return;
    const firstName = visitor !== 'guest' ? (userProfile?.displayName || '').split(' ')[0] : '';
    const ctx = activeTab === 'papers' && selectedExamEntry ? selectedExamEntry : null;
    const starters: ChatAction[] = [];
    if (ctx) starters.push({ label: `What is on ${ctx.shortLabel}?`, run: () => { setMessages((m) => [...m, { from: 'user', text: `What is on ${ctx.shortLabel}?` }, familyAnswer(ctx)]); } });
    if (visitor === 'guest') {
      starters.push({ label: 'Try 5 free questions', run: () => ask('Can I try it for free?') });
      starters.push({ label: 'Prices and plans', run: () => ask('How much does it cost?') });
      starters.push({ label: 'Which exams do you cover?', run: () => ask('Which exams do you cover?') });
    } else if (visitor === 'free') {
      starters.push({ label: 'Prices and plans', run: () => ask('How much does it cost?') });
      starters.push({ label: 'Full mock exams', run: () => ask('Do you have full mock exams?') });
      starters.push({ label: 'Which plan should I choose?', run: () => ask('Which plan should I choose?') });
    } else {
      starters.push({ label: 'Full mock exams', run: () => ask('Do you have full mock exams?') });
      starters.push({ label: 'Study guides', run: () => ask('Do you have study guides?') });
      starters.push({ label: 'Review queue', run: () => ask('What is the review queue?') });
    }
    starters.push({ label: 'Talk to a person', run: showHandoff });
    setMessages([{
      from: 'bot',
      title: firstName ? `Hi ${firstName}, how can we help?` : 'Hi, how can we help?',
      text: 'Ask me about any exam (try a code like 5733 or a subject like biology), our plans, mock exams or your account.',
      actions: starters.slice(0, 4)
    }]);
  };

  // ---------------- UI ----------------
  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex flex-col items-end gap-3">
      {open && (
        <div className="w-[calc(100vw-2rem)] sm:w-[380px] h-[min(600px,calc(100vh-7rem))] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          <div className="bg-gradient-to-br from-[#0f1b4d] to-[#1b2f7a] px-4 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-xl bg-[#0E9594] text-white font-black flex items-center justify-center">P</span>
              <div>
                <div className="text-white font-extrabold text-sm leading-tight">PrePraxis Help</div>
                <div className="text-[11px] text-indigo-100/80">Instant answers, and a person when you need one</div>
              </div>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded-lg text-indigo-100 hover:text-white hover:bg-white/10 cursor-pointer" title="Close chat">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-3.5 py-4 space-y-3 bg-[#f5f6fb]">
            {messages.map((m, i) => (
              <div key={i} className={m.from === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                {m.from === 'user' ? (
                  <div className="max-w-[85%] bg-[#0f1b4d] text-white text-[13px] rounded-2xl rounded-br-md px-3.5 py-2.5 whitespace-pre-line">{m.text}</div>
                ) : (
                  <div className="max-w-[92%] space-y-2">
                    <div className="bg-white border border-slate-200 text-slate-700 text-[13px] rounded-2xl rounded-bl-md px-3.5 py-2.5 shadow-xs">
                      {m.title && <div className="font-extrabold text-[#0f1b4d] mb-1">{m.title}</div>}
                      <div className="whitespace-pre-line leading-relaxed">{m.text}</div>
                    </div>
                    {m.actions && m.actions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {m.actions.map((a, j) => (
                          <button
                            key={j}
                            type="button"
                            onClick={a.run}
                            className={`text-[12px] font-bold px-3 py-1.5 rounded-full border transition cursor-pointer flex items-center gap-1 ${
                              a.primary
                                ? 'bg-[#0E9594] border-[#0E9594] text-white hover:bg-[#0b7f7e]'
                                : 'bg-white border-slate-300 text-[#0f1b4d] hover:border-[#0E9594]'
                            }`}
                          >
                            {a.label} {a.primary && <ArrowRight className="w-3 h-3" />}
                          </button>
                        ))}
                      </div>
                    )}
                    {m.showHandoffForm && formState !== 'sent' && i === messages.length - 1 && (
                      <form onSubmit={submitHandoff} className="bg-white border border-slate-200 rounded-2xl p-3 space-y-2">
                        <div className="text-[12px] font-extrabold text-[#0f1b4d] flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Leave a message</div>
                        <input
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="Your name"
                          className="w-full border border-slate-200 rounded-lg px-2.5 py-2 text-[12px] focus:outline-none focus:ring-2 focus:ring-[#0E9594]"
                        />
                        <input
                          value={form.contact}
                          onChange={(e) => setForm({ ...form, contact: e.target.value })}
                          placeholder="Email or WhatsApp number"
                          required
                          className="w-full border border-slate-200 rounded-lg px-2.5 py-2 text-[12px] focus:outline-none focus:ring-2 focus:ring-[#0E9594]"
                        />
                        <textarea
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                          placeholder="How can we help?"
                          required
                          rows={3}
                          className="w-full border border-slate-200 rounded-lg px-2.5 py-2 text-[12px] focus:outline-none focus:ring-2 focus:ring-[#0E9594] resize-none"
                        />
                        <button
                          type="submit"
                          disabled={formState === 'sending'}
                          className="w-full bg-[#0f1b4d] hover:bg-[#1b2f7a] text-white font-bold text-[12px] py-2 rounded-lg flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-60"
                        >
                          {formState === 'sending' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                          Send message
                        </button>
                      </form>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); ask(input); }}
            className="border-t border-slate-200 p-2.5 flex items-center gap-2 bg-white"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about an exam, plans or your account"
              maxLength={500}
              className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0E9594]"
            />
            <button type="submit" className="w-10 h-10 rounded-xl bg-[#0E9594] hover:bg-[#0b7f7e] text-white flex items-center justify-center cursor-pointer" title="Send">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={openWhatsApp}
          className="flex items-center gap-2 px-3.5 py-2.5 sm:px-4 rounded-full shadow-lg hover:shadow-xl bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold text-xs cursor-pointer"
          title={`WhatsApp ${WHATSAPP_DISPLAY}`}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.711 2.598 2.664-.699c.981.564 1.769.877 2.796.877 3.182 0 5.768-2.587 5.768-5.766.001-3.187-2.575-5.763-5.768-5.763zm3.435 8.163c-.144.405-.837.774-1.17.824-.312.045-.698.077-2.274-.582-1.782-.746-2.92-2.577-3.008-2.695-.088-.118-.718-.957-.718-1.826 0-.868.452-1.297.613-1.472.161-.174.352-.218.47-.218.118 0 .237.002.34.007.108.005.253-.041.395.301.145.351.498 1.216.541 1.304.043.088.072.19.014.305-.058.117-.087.19-.174.293-.088.103-.186.23-.266.309-.088.088-.18.185-.077.362.103.177.46 1.026 1.077 1.576.793.707 1.462.925 1.669 1.028.207.103.328.088.45-.052.122-.14.523-.609.663-.817.14-.208.28-.174.471-.103.19.072 1.209.57 1.417.674.208.103.346.155.397.243.05.088.05.512-.094.917z" />
          </svg>
          <span className="hidden sm:inline">WhatsApp</span>
        </button>
        <button
          type="button"
          onClick={openChat}
          className={`flex items-center gap-2 px-3.5 py-2.5 sm:px-4 rounded-full shadow-lg hover:shadow-xl font-bold text-xs cursor-pointer text-white ${open ? 'bg-slate-800' : 'bg-[#0f1b4d] hover:bg-[#1b2f7a]'}`}
          title="Chat with PrePraxis"
        >
          {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
          <span className="hidden sm:inline">{open ? 'Close chat' : 'Live chat'}</span>
        </button>
      </div>
    </div>
  );
};
